import { useState, useEffect } from "react";
import { ReactComponent as HelpCenterGirl3 } from "../../assets/HelpCenter/HelpCenterGirl3.svg";
import { ReactComponent as EventHost } from "../../assets/HelpCenter/Event_Host.svg";
import { ReactComponent as Performer } from "../../assets/HelpCenter/Performer.svg";
import { ReactComponent as Crew } from "../../assets/HelpCenter/Crew.svg";
import { ReactComponent as Planner } from "../../assets/HelpCenter/Planner.svg";
import { ArticleCard } from "./ArticleCard";
import { ContactBanner } from "./ContactBanner";
import { useNavigate } from "react-router-dom";
import { HelpCenterArticleSearchBar } from "./searchBar.jsx";
import { useSelector } from "react-redux";
import headphoneImg from "../../assets/HelpCenter/7aef645ad8eedb76a524fb5eaf8b15235cfa6bca.png";
import handPhoneImg from "../../assets/HelpCenter/e7d617f7d9d2602ce5771a6748166ead262c29c4.png";
import rectangleBg from "../../assets/HelpCenter/Rectangle 4776.svg";
import starBg from "../../assets/HelpCenter/Star 1.svg";

const ArrowRight = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

const roles = [
  {
    id: "host",
    title: "Event Host",
    desc: "Manage events, proposals, and bookings",
    icon: <EventHost className="w-7 h-7" />,
    links: ["Getting Started", "Posting a Requirement", "Booking and Payments"],
  },
  {
    id: "planner",
    title: "Planner",
    desc: "Organize services, bookings, and workflow",
    icon: <Planner className="w-7 h-7" />,
    links: ["Account Creation", "Portfolio and Profile", "Packages"],
  },
  {
    id: "performer",
    title: "Performer",
    desc: "Handle visibility, bookings, and payouts",
    icon: <Performer className="w-7 h-7" />,
    links: ["Account Creation", "Portfolio and Profile", "Packages"],
  },
  {
    id: "crew",
    title: "Crew",
    desc: "Coordinate tasks, bookings, and support",
    icon: <Crew className="w-7 h-7" />,
    links: ["Bookings", "Portfolio and Profile", "Reliability and Strikes"],
  },
];

const HeroSection = () => {
  const navigate = useNavigate();

  const handleTagClick = (tag) => {
    const value = encodeURIComponent(tag);
    navigate(`/help/search?key=${value}`);
  };

  return (
    <div className="relative mx-3 sm:mx-6 md:mx-8 mt-24 sm:mt-28 lg:mt-32 rounded-[28px] sm:rounded-[36px] bg-[#FF4C18] px-4 sm:px-8 lg:px-12 py-10 sm:py-14 lg:py-16 overflow-hidden flex flex-col items-center justify-center min-h-[340px] sm:min-h-[380px] lg:min-h-[400px] shadow-lg">
      
      {/* ── Top-Right Background Asset ── */}
      <img
        src={rectangleBg}
        alt=""
        className="absolute top-0 right-0 pointer-events-none z-0 max-w-[20%] sm:max-w-[20%] lg:max-w-[20%]"
      />

      {/* ── Bottom-Left Background Asset ── */}
      <img
        src={starBg}
        alt=""
        className="absolute bottom-0 left-0 pointer-events-none z-0 max-w-[20%] sm:max-w-[20%] lg:max-w-[20%]"
      />

      {/* ── Left Illustration: Headphone ── */}
      <img
        src={headphoneImg}
        alt="Headphones"
        className="hidden md:block absolute -left-6 lg:-left-52 top-1/3 -translate-y-1/2 h-[70%] md:h-[70%] lg:h-[70%] max-h-[380px] object-contain pointer-events-none z-10"
      />

      {/* ── Right Illustration: Hand Phone ── */}
      <img
        src={handPhoneImg}
        alt="Contact Support"
        className="hidden md:block absolute -right-6 lg:-right-16 bottom-0 h-[80%] md:h-[80%] lg:h-[80%] max-h-[400px] object-contain pointer-events-none z-10 -rotate-45"
      />

      {/* ── Center Content Stack ── */}
      <div className="relative z-20 max-w-xl w-full flex flex-col items-center text-center">
        {/* Heading */}
        <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight mb-2 sm:mb-3">
          How can we help you?
        </h1>

        {/* Subtitle */}
        <p className="text-white/95 text-xs sm:text-base font-medium mb-6 sm:mb-8 leading-relaxed max-w-md">
          Find answers, manage bookings, or get support
        </p>

        {/* Search Bar Input Container */}
        <div className="relative w-full max-w-lg bg-white rounded-full px-4 py-2.5 sm:px-5 sm:py-3.5 shadow-md flex items-center gap-3 border border-transparent focus-within:ring-4 focus-within:ring-white/30 transition-all">
          <HelpCenterArticleSearchBar placeholder="Search FAQs, Guides or Contact Support." />
        </div>

        {/* Top Searches Row */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5 mt-5 sm:mt-6">
          <span className="text-white font-extrabold text-xs sm:text-sm mr-1">
            Top searches
          </span>
          {["Event day", "Account", "Booking", "Payment"].map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => handleTagClick(tag)}
              className="bg-white hover:bg-orange-50 text-gray-700 hover:text-[#FF4D12] font-semibold text-xs sm:text-sm px-4 py-1.5 rounded-full transition shadow-xs active:scale-95 whitespace-nowrap"
            >
              {tag}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const RoleCard = ({ role }) => {
  const navigate = useNavigate();
  const handleViewAllClick = () => {
    const value = role.title.split(" ").join("%20");
    navigate(`/help/by-role?role=${value}`);
  };
  const handleLinkClick = (link) => {
    const category = link.split(" ").join("%20");
    const roleValue = role.title.split(" ").join("%20");
    navigate(`/help/by-role?role=${roleValue}&category=${category}`);
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        {role.icon}
        <div>
          <h3 className="font-semibold text-gray-900">{role.title}</h3>
          <p className="text-xs text-gray-400">{role.desc}</p>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {role.links.map((link) => (
          <button
            key={link}
            className="flex items-center justify-between text-sm text-gray-700 border border-gray-200 rounded-lg px-4 py-2.5 hover:bg-gray-50 hover:border-gray-300 transition-colors"
            onClick={() => handleLinkClick(link)}
          >
            <span>{link}</span>
            <ArrowRight />
          </button>
        ))}
      </div>
      <button
        className="text-sm text-orange-500 hover:text-orange-600 font-medium flex items-center gap-1 mt-1 self-end transition-colors"
        onClick={handleViewAllClick}
      >
        View all <ArrowRight />
      </button>
    </div>
  );
};

const BrowseByRoles = () => (
  <section className="px-4 md:px-8 py-8">
    <h2 className="text-3xl font-bold text-gray-900 mb-5">Browse by Roles</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {roles.map((role) => (
        <RoleCard key={role.id} role={role} />
      ))}
    </div>
  </section>
);

const FeaturedArticles = ({ featuredArticles }) => (
  <section className="px-4 md:px-8 py-8">
    <div className="flex items-end justify-between mb-2">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">
          Featured Top Articles
        </h2>
        <p className="text-xs text-gray-400 mt-1">
          Quick answers to most common questions.
        </p>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
      {featuredArticles.map((article) => (
        <ArticleCard key={article._id} article={article} />
      ))}
    </div>
  </section>
);
export default function HelpCenterHome() {
  const allArticles = useSelector((state) => state.helpCenterArticles);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  useEffect(() => {
    const featuredArr = allArticles.slice(0, 6); // Get top 6 articles for featured section
    setFeaturedArticles(featuredArr);
  }, [allArticles]);

  return (
    <div className="min-h-screen bg-white font-sans">
      <main className="max-w-4xl mt-16 mx-auto">
        <HeroSection />
        <BrowseByRoles />
        <FeaturedArticles featuredArticles={featuredArticles} />
        <ContactBanner />
      </main>
    </div>
  );
}
